import { watchPreviewRoot } from './editor-preview-root.js';
import { initModalDismissal } from './modal-behavior.js';

const EVENT_ADMIN_PANEL_STORE = 'jctc/event-admin-panel';

const getAjaxUrl = () => window.jctc_vars?.ajax_url || '/wp-admin/admin-ajax.php';
const getNonce = () => window.jctc_vars?.nonce || '';

// Generalized from card.closest('.js-events-grid') -- the upcoming-events
// grid was the only source of a group_series flag when this convention was
// introduced, but the calendar (template-parts/event-calendar-grid.php) and
// All Events carousel sections (template-parts/event-carousel-section.php)
// now carry the same data-group-series attribute on their own wrapper, so
// this reads whichever ancestor actually has it instead of a class name
// specific to one caller.
const getGroupSeriesFlag = (card) =>
    card.closest('[data-group-series]')?.dataset.groupSeries === '0' ? '0' : '1';

// Block-editor-preview-only: the sidebar panel itself always lives in the
// OUTER post-edit screen chrome (assets/src/js/admin/index.js,
// enqueue_block_editor_assets only), never inside the block editor's own
// canvas. A prior version of this comment assumed the canvas only renders as
// an iframe once every visible block declares apiVersion 3+, and that this
// theme's still-apiVersion-2 ACF blocks therefore rendered in the SAME
// window as that chrome -- a later WordPress core update made the canvas
// always render inside `iframe[name="editor-canvas"]` regardless of block
// apiVersion, which is why this function reaches across to window.parent's
// wp.data instead of assuming same-window access (same-origin iframes share
// full window access, no postMessage bridge needed). Falls back to the local
// window when the canvas genuinely isn't iframed (a future editor version,
// or a caller running outside wp-admin entirely -- see this function's own
// callers' watchPreviewRoot() attachment for the other half of coping with
// the canvas now always being iframed: WordPress duplicates enqueued STYLES
// into that iframe but never SCRIPTS, so this module's own click/keydown
// listeners need a second attachment directly inside that iframe's document,
// not just this store-reaching logic). Returns false (a no-op) on the real
// front end, where there is no editor at all, and harmlessly wherever the
// panel's store was never registered.
//
// The panel itself is InspectorControls (Block tab), not
// PluginDocumentSettingPanel (Page tab) -- InspectorControls only renders
// while its own block is the selected block, so a plain store dispatch isn't
// enough on its own; explicitly selecting the block a card belongs to is
// what actually makes the Block tab (and this panel inside it) show up.
// data-block="<clientId>" is core Gutenberg's own DOM contract for this
// (block-editor-block-list__block wrapper), the same one wp-swiper's own
// block editor bundle relies on for its per-instance styling.
const openAdminEventPanel = (eventId, cardEl) => {
    const targetWp = window.parent !== window ? window.parent.wp : window.wp;
    if (!eventId || !targetWp?.data?.dispatch) {
        return false;
    }

    const clientId = cardEl?.closest('[data-block]')?.getAttribute('data-block');
    if (clientId) {
        targetWp.data.dispatch('core/block-editor')?.selectBlock?.(clientId);
    }

    targetWp.data.dispatch(EVENT_ADMIN_PANEL_STORE).openPanel(eventId);
    return true;
};

// Counterpart to openAdminEventPanel() above: whenever a click lands
// somewhere in the admin editor that ISN'T an event card, drop the event
// fields panel back to the block's own general settings (see that
// function's own doc comment for why "card clicked" vs. "block selected
// some other way" can only be told apart by where the click actually
// landed, not by React state/isSelected). Checking the store's own eventId
// first keeps this a no-op on every ordinary click across the whole admin
// UI when no event panel is even open, and excluding clicks inside the
// panel's own markup (.jctc-event-admin-panel) keeps using its Save/Close
// button or typing into one of its fields from closing itself out from
// under the admin mid-edit.
// event.composedPath(), not event.target.closest(...): a click on a button
// whose own onClick causes React to re-render and swap it out (exactly what
// CountryQuickAddControl's own "Add Country" toggle does, replacing itself
// with a text input) can leave `event.target` DETACHED from the document by
// the time this native document-level listener runs in the bubble phase --
// React's own synthetic event handling fires first, synchronously, since
// its root listener sits further down the DOM tree than `document`.
// `.closest()` on a detached node has no parentElement chain left to walk,
// so it always returns null there, even for a click that really did happen
// inside the panel -- confirmed in the field as exactly this button's click
// closing the whole panel instead of expanding into its own input. `
// composedPath()` captures the full ancestor chain at DISPATCH time, before
// any handler (React's or this one) has a chance to mutate the DOM, so it
// stays correct regardless of what the click's own handlers do afterward.
const clickPathHasClass = (event, className) =>
    event.composedPath().some((node) => node.classList?.contains(className));

const closeAdminEventPanelIfOpen = (event) => {
    if (clickPathHasClass(event, 'jctc-event-admin-panel')) {
        return;
    }
    const targetWp = window.parent !== window ? window.parent.wp : window.wp;
    const store = targetWp?.data;
    if (!store?.select || !store?.dispatch) {
        return;
    }
    if (store.select(EVENT_ADMIN_PANEL_STORE)?.getEventId?.()) {
        store.dispatch(EVENT_ADMIN_PANEL_STORE).closePanel();
    }
};

// Admin-preview-only: a click on empty space inside a Swiper-based block
// (the Recommended Events carousel's own dark background/track, not a
// card) never reached Gutenberg's native block-selection handling --
// Swiper's own mouse/touch handling on that track swallowed the mousedown
// before the click could bubble up to it, same root cause documented in
// blocks/recommended-events/render.php's own allowTouchMove:false comment.
// That CSS change alone should stop the swallowing, but explicitly
// selecting the block here too is a direct, low-cost fix for the exact
// symptom reported (clicking the empty area didn't open the block's
// settings) that doesn't depend on it.
const selectAncestorBlockFromClick = (event) => {
    const targetWp = window.parent !== window ? window.parent.wp : window.wp;
    if (!targetWp?.data?.dispatch) {
        return;
    }
    // composedPath(), not event.target.closest() -- see clickPathHasClass's
    // own doc comment above for why a click that triggers its own React
    // re-render can leave event.target detached by the time this runs.
    const blockNode = event.composedPath().find((node) => node.hasAttribute?.('data-block'));
    const clientId = blockNode?.getAttribute('data-block');
    if (clientId) {
        targetWp.data.dispatch('core/block-editor')?.selectBlock?.(clientId);
    }
};

const initJctcScrollbars = (root) => {
    const scrollAreas = root.querySelectorAll(
        '.event-modal__description, .event-modal__schedule-rows'
    );
    const instances = [];

    const removeInstance = (instance) => {
        instance.area.classList.remove('has-jctc-scrollbar');
        instance.bar.remove();
        instance.area.removeEventListener('scroll', instance.update);
    };

    const createInstance = (area) => {
        const bar = document.createElement('div');
        const thumb = document.createElement('div');
        const isSchedule = area.classList.contains('event-modal__schedule-rows');
        bar.className = isSchedule ? 'jctc-scrollbar jctc-scrollbar--schedule' : 'jctc-scrollbar';
        thumb.className = 'jctc-scrollbar__thumb';
        bar.setAttribute('aria-hidden', 'true');
        bar.append(thumb);
        root.append(bar);
        area.classList.add('has-jctc-scrollbar');

        const update = () => {
            const rect = area.getBoundingClientRect();
            const panelRect = area.closest('.event-modal__panel')?.getBoundingClientRect();
            const isSchedule = area.classList.contains('event-modal__schedule-rows');
            const barWidth = isSchedule ? 6 : 8;
            const trackInset = isSchedule ? 0 : 2;
            const top = panelRect ? Math.max(rect.top, panelRect.top) : rect.top;
            const bottom = panelRect ? Math.min(rect.bottom, panelRect.bottom) : rect.bottom;
            const height = Math.max(0, bottom - top);
            const trackHeight = Math.max(0, height - trackInset * 2);
            const maxScroll = Math.max(1, area.scrollHeight - area.clientHeight);
            const thumbHeight = Math.max(24, trackHeight * (area.clientHeight / area.scrollHeight));
            const maxThumbTop = Math.max(0, trackHeight - thumbHeight);
            const thumbTop = trackInset + maxThumbTop * (area.scrollTop / maxScroll);
            const unclampedLeft = rect.right - barWidth;
            const left = panelRect
                ? Math.min(Math.max(unclampedLeft, panelRect.left), panelRect.right - barWidth)
                : unclampedLeft;

            bar.style.left = `${left}px`;
            bar.style.top = `${top}px`;
            bar.style.height = `${height}px`;

            thumb.style.height = `${thumbHeight}px`;
            thumb.style.transform = `translateY(${thumbTop}px)`;
        };

        const instance = { area, bar, update };
        area.addEventListener('scroll', update, { passive: true });
        update();
        instances.push(instance);
    };

    const refresh = () => {
        instances.splice(0).forEach(removeInstance);
        scrollAreas.forEach((area) => {
            if (area.scrollHeight > area.clientHeight + 1) {
                createInstance(area);
            }
        });
    };

    refresh();
    window.addEventListener('resize', refresh, { passive: true });

    return () => {
        window.removeEventListener('resize', refresh);
        instances.splice(0).forEach(removeInstance);
    };
};

export default function initEvents() {
    const modal = document.querySelector('.js-event-modal');
    const modalContent = modal?.querySelector('.js-modal-content');
    // The modal markup only exists on the front end (rendered in footer.php).
    // It is absent in the block-editor admin screen, which has no template
    // footer -- but admin still needs the click handler below for the
    // click-to-edit-in-admin feature on event cards, so the modal-specific
    // logic is gated per-branch instead of bailing out of the whole function.
    const hasModal = !!(modal && modalContent);

    let destroyScrollbars = null;

    // Close behavior (outside-click/Escape/close-button/focus-trap/
    // focus-return) is owned by modules/modal-behavior.js, shared with
    // booking-form-modal.js, per JCTCTAS-70 -- this file only owns opening
    // the modal (AJAX content fetch below) and its own scrollbar/content
    // cleanup on close.
    const modalController = hasModal
        ? initModalDismissal({
              modal,
              panelSelector: '.event-modal__panel',
              closeSelector: '.js-event-modal-close',
              onClose: () => {
                  destroyScrollbars?.();
                  destroyScrollbars = null;
                  modalContent.innerHTML = '';
              },
          })
        : null;

    const renderLoadingState = () => {
        modalContent.innerHTML = '<div class="event-modal__loading">Loading event details...</div>';
    };

    // groupSeries mirrors events-ajax.js's own grid.dataset.groupSeries
    // convention ('0' explicitly ungrouped, everything else grouped) -- the
    // card that was clicked already rendered under one or the other mode
    // (blocks/upcoming-events/render.php's data-group-series attribute on
    // its own grid), so the modal it opens must agree, or a single-day
    // sibling event opened while ungrouped would show the OTHER sibling's
    // date(s) and a "SELECT A DATE" prompt that no longer makes sense.
    const fetchModalContent = async (eventId, groupSeries = '1', trigger = null) => {
        renderLoadingState();
        modalController?.open(trigger);

        const formData = new FormData();
        formData.append('action', 'get_event_modal');
        formData.append('id', String(eventId));
        formData.append('group_series', groupSeries);
        formData.append('nonce', getNonce());

        try {
            const response = await fetch(getAjaxUrl(), {
                method: 'POST',
                body: formData,
            });
            const result = await response.json();

            if (!result.success || !result.data?.html) {
                throw new Error(result.data?.message || 'Modal content was not returned.');
            }

            modalContent.innerHTML = result.data.html;
            destroyScrollbars?.();
            destroyScrollbars = initJctcScrollbars(modalContent);
            // Initial focus on open, once real content (not the loading state)
            // is in the DOM -- the close button is always present regardless of
            // which CTA variant (GET TICKETS link vs. SELECT A DATE button) this
            // event ends up rendering.
            modalContent.querySelector('.event-modal__close')?.focus();
        } catch (error) {
            console.error('Event modal load failed:', error);
            modalContent.innerHTML =
                '<div class="event-modal__loading event-modal__loading--error">Unable to load event details.</div>';
        }
    };

    // Extracted (not an inline arrow passed straight to addEventListener) so
    // the exact same click-handling logic can also be attached to the block
    // editor's iframed canvas document below -- see the watchPreviewRoot()
    // call after this listener's own registration for why a second
    // attachment is needed at all.
    const handleDocumentClick = (event) => {
        if (hasModal) {
            // template-parts/event-modal-content.php only renders this <button> (not a
            // real <a>) when the event's dates each have their own distinct
            // ticket_link ($needs_date_selection) -- there is deliberately no
            // single URL to navigate to here. Moves focus to the first
            // clickable date row instead; the browser's native "scroll
            // focused element into view" behavior (no explicit scrollIntoView
            // call needed) reveals it even if &__schedule-rows' own internal
            // scrollbar (many dates) currently has it scrolled out of view.
            // The aria-live region covers screen reader users, who wouldn't
            // otherwise know why this button didn't navigate like GET
            // TICKETS normally does.
            const selectDateTrigger = event.target.closest('.event-modal__cta-btn--select-date');
            if (selectDateTrigger) {
                event.preventDefault();
                const panel = selectDateTrigger.closest('.event-modal__panel');
                const liveRegion = panel?.querySelector('.js-event-modal-live-region');
                if (liveRegion) {
                    liveRegion.textContent =
                        'This event has multiple dates. Choose a date below to continue to tickets.';
                }
                const firstDateLink = panel?.querySelector('.event-modal__schedule-row--link');
                if (firstDateLink) {
                    // .is-guided (_modal.scss), not just the .focus() call itself
                    // -- see that class's own doc comment for why a
                    // mouse-click-triggered .focus() doesn't get Chromium's
                    // native :focus-visible ring on its own. Cleared on the
                    // element's next blur so it doesn't linger once the user has
                    // actually interacted with it (Tab away, click elsewhere).
                    firstDateLink.classList.add('is-guided');
                    firstDateLink.addEventListener(
                        'blur',
                        () => firstDateLink.classList.remove('is-guided'),
                        { once: true }
                    );
                    firstDateLink.focus();
                }
                return;
            }
        }

        const cardCta = event.target.closest('.js-event-card-cta');
        if (cardCta) {
            return;
        }

        const card = event.target.closest('.js-event-card');
        if (!card) {
            // Cheap upfront guard -- both functions below only ever do
            // anything once `wp` actually exists (the block editor's own
            // outer chrome; see their own doc comments), which is never true
            // on the live front end. Resolving it once here first lets an
            // ordinary front-end click skip both calls entirely, instead of
            // paying for closeAdminEventPanelIfOpen's own full
            // event.composedPath() ancestor-chain walk before it discovers
            // the same thing on its own -- on every single click anywhere on
            // the live site, not just event cards.
            const targetWp = window.parent !== window ? window.parent.wp : window.wp;
            if (targetWp) {
                closeAdminEventPanelIfOpen(event);
                selectAncestorBlockFromClick(event);
            }
            return;
        }

        event.preventDefault();

        // Block-editor-preview-only: card.dataset.adminEditUrl is only ever
        // present when template-parts/event-card.php rendered with
        // is_admin_preview true, which never happens on the live front end.
        // Opens the sidebar panel in the outer editor window instead of
        // navigating away from the page being edited (see
        // openAdminEventPanel above).
        if (card.dataset.adminEditUrl && openAdminEventPanel(card.dataset.eventId, card)) {
            return;
        }

        // A real past jctc_event_page post rendered via event-card.php (the
        // Upcoming Events grid's admin-only test-card preview, see
        // blocks/upcoming-events/render.php) carries a real, permanent,
        // indexable permalink (data-personal-page-url) instead of a live
        // post ID -- navigate straight there rather than opening the
        // live-event AJAX modal, which would 404 (get_event_modal hard-checks
        // post_type === 'seetickets-event').
        if (card.dataset.personalPageUrl) {
            window.location.href = card.dataset.personalPageUrl;
            return;
        }

        if (!hasModal) {
            return;
        }

        const eventId = card.dataset.eventId;
        if (!eventId) {
            return;
        }

        fetchModalContent(eventId, getGroupSeriesFlag(card), card);
    };

    document.addEventListener('click', handleDocumentClick);

    // Block-editor-preview-only: card.dataset.adminEditUrl (checked inside
    // handleDocumentClick above) is only ever present in ACF's ServerSideRender
    // preview, which since a WordPress core update now always renders inside
    // `iframe[name="editor-canvas"]` -- editor-preview-root.js's own doc
    // comment ("WordPress duplicates enqueued STYLES into that iframe, never
    // SCRIPTS") already established the general shape of this bug for the
    // admin card-scaling shrink and the recommended-card glow; this module's
    // own click-to-configure-in-admin feature had the exact same gap and just
    // hadn't been ported to watchPreviewRoot() yet. Without this, the
    // document.addEventListener('click', ...) call above only ever binds to
    // the OUTER window's document -- a real click on a card rendered inside
    // the iframe fires entirely within that iframe's own document and never
    // bubbles out to the outer one, so openAdminEventPanel() (and the
    // existing modal/front-end code paths, which simply no-op here since
    // hasModal is false in the editor) never ran for a genuine user click,
    // confirmed live: dispatching the identical click event directly on the
    // iframe's own document did nothing until this second attachment existed.
    watchPreviewRoot((doc) => {
        doc.addEventListener('click', handleDocumentClick);
    });

    const handleDocumentKeydown = (event) => {
        // Same exclusion as the click handler above: the real 'GET TICKETS'
        // <a> is nested inside .js-event-card, so a focused Enter/Space here
        // must be left to the link's own native activation (navigate to
        // event.ticket_url) instead of being hijacked into opening the
        // details modal -- confirmed live as exactly that hijack before this
        // check existed (event.target.closest('.js-event-card') below still
        // matches the outer article, so without this the modal opened and
        // the ticket link was never reachable by keyboard at all).
        if (event.target.closest('.js-event-card-cta')) {
            return;
        }

        const card = event.target.closest('.js-event-card');
        if (!card) {
            return;
        }

        if (event.key !== 'Enter' && event.key !== ' ') {
            return;
        }

        event.preventDefault();

        if (card.dataset.adminEditUrl && openAdminEventPanel(card.dataset.eventId, card)) {
            return;
        }

        if (card.dataset.personalPageUrl) {
            window.location.href = card.dataset.personalPageUrl;
            return;
        }

        if (!hasModal) {
            return;
        }

        const eventId = card.dataset.eventId;
        if (eventId) {
            fetchModalContent(eventId, getGroupSeriesFlag(card), card);
        }
    };

    document.addEventListener('keydown', handleDocumentKeydown);

    // Same iframe gap as the click listener above -- a keyboard Enter/Space
    // on a card rendered inside the editor's iframed canvas never reaches the
    // outer document either.
    watchPreviewRoot((doc) => {
        doc.addEventListener('keydown', handleDocumentKeydown);
    });
}
